import { observer } from "mobx-react";
import React from "react";
import Chip from "../../../../../components/chip/chip";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FontBody2 } from "../../../../../components/fonts/font-body2/font-body2";
import FontBody4 from "../../../../../components/fonts/font-body4/font-body4";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import IconCircleChargedFees from "../../../../../components/icons/icon-circle-charged-fees/icon-circle-charged-fees";
import IconCircleExchange from "../../../../../components/icons/icon-circle-exchange/icon-circle-exchange";
import Icon from "../../../../../components/icons/icon/icon";
import GridLine from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineItem from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import { Tooltip } from "../../../../../components/tooltip/tooltip";
import RmaExchangeOrderStore from "../../stores/rma-exchange-order-store";
import DraftOrderStateChip from "./draft-order-state-chip";
import "./rma-payments-exchanges-card.scss";
import CustomerCaseStore from "../../stores/customer-case-store";
import Currency from "../../../../../components/currency/currency";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";

interface IDraftOrderGridLineProps {
    store: RmaExchangeOrderStore;
    customerCaseStore: CustomerCaseStore;
    storeCurrencyCode: string;
}

const DraftOrderGridLine: React.FC<IDraftOrderGridLineProps> = observer(({ store, customerCaseStore, storeCurrencyCode }) => {
    
    const { getTranslation } = useDashboardTranslation();
    const reservedItemsInfo = store.getReservedItemsInfo(getTranslation);
    const pageScope = 'dashboard-returns-rma-card';
    return (
        <GridLine
            key={`rma_page_draft_order_${store.draftOrderId}`}
            settings={{
                columns: [{ width: 200, type: 'px' }, { width: 1 }, { width: 1 }, { width: 1 }, { width: 100, type: 'px' }]
            }}
            columns={[
                <GridLineItem>
                    <div className={'rma-payments-exchanges'}>
                        <Icon Component={store.isExchange() ? IconCircleExchange : IconCircleChargedFees} />
                        <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation(store.getTypeText(), pageScope)}</FontBody1>
                    </div>
                </GridLineItem>,
                <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('ORDER', pageScope)} #</FontBody4>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            <a href={store.orderLink ?? store.draftOrderLink} target={'_blank'} rel={'noreferrer'}>
                                {store.orderName}
                            </a>
                        </FontBody4>
                    </div>
                </GridLineItem>,
                store.isExchange() ? (<GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('BONUS', pageScope)}</FontBody4>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            <Currency
                                currencyCode={customerCaseStore.paymentCurrencyCode}
                                amount={+store.totalBonus}
                            />
                        </FontBody4>
                    </div>
                </GridLineItem>) : store.isFee() ? <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('PAYMENT_COLUMN_TITLE_FOR_TOTAL', pageScope)}</FontBody4>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            <Currency
                                currencyCode={storeCurrencyCode}
                                amount={store.totalAmountAtTheTimeOfDraftOrderCreation}
                            />
                        </FontBody4>
                    </div>
                </GridLineItem>: null ,
                <GridLineItem>
                    {reservedItemsInfo &&
                        <div className={'rma-payments-exchanges-content'}>
                            <Tooltip variant={reservedItemsInfo.tooltipVariant}
                                content={<div className="reserve-items-indicator-tooltip"><FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>{reservedItemsInfo.tooltipText}</FontBody2></div>}>
                                <Chip color={reservedItemsInfo.chipColor} label={reservedItemsInfo.chipLabel} />
                            </Tooltip>
                        </div>
                    }
                </GridLineItem>,
                <GridLineItem>
                    <div className={'rma-payments-exchanges-content'}>
                        <FontBody4 color={FONT_COLOR.PRIMARY3_50}>
                            <DraftOrderStateChip state={store.internalState} isExchangeReleased={store.isExchangeReleased()} />
                        </FontBody4>
                    </div>
                </GridLineItem>,
            ]}
        />
    )
});

export default DraftOrderGridLine;